import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import {
  SubscriptionDetailDto,
  SubscriptionDetailsDto,
} from 'api/Trading-Accounts/Types/SubscriptionDetailsDto'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchTradingAccountSubscriptionDetailTypesAction } from 'store/trading-accounts/trading-accounts.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { isEmpty } from 'utils/is-empty'

import { subscriptionDetailColumns } from './subscriptionDetailColumns'
import {
  SubscriptionsDetailsMainForm,
  SubscriptionsDetailsMainValues,
} from './SubscriptionsDetailsForm'
import { SubscriptionTab } from './SubscriptionSlug'

interface SubscriptionsDetailsMainProps {
  subscriptionDetails: SubscriptionDetailDto[]
  onSubscriptionAddModalOpen(): void
  onSubscriptionEditModalOpen(data: SubscriptionDetailDto): void
  onSubscriptionDeleteModalOpen(data: SubscriptionDetailDto): void
}

export const SubscriptionsDetailsMainTable: React.FC<SubscriptionsDetailsMainProps> = (props) => {
  const { subscriptionDetails, onSubscriptionEditModalOpen, onSubscriptionDeleteModalOpen } = props

  return (
    <div>
      <Grid resizable data={subscriptionDetails} pageable style={{ zIndex: 0 }}>
        {subscriptionDetailColumns
          .filter((c) => c.show !== false)
          .map((column, idx) => (
            <Column key={idx + 2} {...column} />
          ))}
        <Column
          width={40}
          title=''
          cell={(gridCellProps: GridCellProps) => (
            <td>
              <MoreButton
                items={[
                  {
                    name: 'Edit',
                    'data-test': 'edit',
                    onClick: () => onSubscriptionEditModalOpen(gridCellProps.dataItem),
                  },
                  {
                    name: 'Delete',
                    'data-test': 'delete',
                    onClick: () => onSubscriptionDeleteModalOpen(gridCellProps.dataItem),
                  },
                ]}
                data-test='more-button'
              />
            </td>
          )}
        />
      </Grid>
    </div>
  )
}

const useMapStateToProps = () => ({
  tradingAccountSubscriptionTypes: useAppSelector(
    (state) => state.tradingAccounts.tradingAccountSubscriptionDetailTypes
  ),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  return {
    getTradingAccountSubscriptionDetailTypes: async () =>
      dispatch(fetchTradingAccountSubscriptionDetailTypesAction(tradingAccountsApiClient)),
  }
}

interface OuterProps {
  subscription: SubscriptionDetailsDto
  onRefresh(): void
}

interface MatchParams {
  id: string
  slug: SubscriptionTab
  [key: string]: string | undefined
}

export const SubscriptionsDetailsMainPage: React.FC<OuterProps> = (props) => {
  const { subscription: subscriptionDetails, onRefresh } = props
  const { id: subscriptionId } = useParams<MatchParams>()
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const [subscription, setSubscription] = useState<SubscriptionDetailDto>()
  const [isSubscriptionAddModalOpen, setSubscriptionAddModalOpen] = useState<boolean>(false)
  const [isSubscriptionEditModalOpen, setSubscriptionEditModalOpen] = useState<boolean>(false)
  const [isSubscriptionDeleteModalOpen, setSubscriptionDeleteModalOpen] = useState<boolean>(false)

  const { tradingAccountSubscriptionTypes } = useMapStateToProps()
  const { getTradingAccountSubscriptionDetailTypes } = useMapDispatchToProps()

  useEffect(() => {
    if (isEmpty(tradingAccountSubscriptionTypes)) getTradingAccountSubscriptionDetailTypes()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitForm = async (subscriptionDetail: SubscriptionsDetailsMainValues) => {
    if (subscriptionDetail?.id) {
      await tradingAccountsApiClient.updateTradingAccountSubscription(
        subscriptionDetail.id,
        subscriptionDetail
      )
    } else {
      if (!subscriptionId) return
      await tradingAccountsApiClient.createTradingAccountSubscription({
        ...subscriptionDetail,
        subscriptionId,
      })
    }

    setSubscription(undefined)
    setSubscriptionAddModalOpen(false)
    setSubscriptionEditModalOpen(false)
    await onRefresh()
  }

  const handleSubscriptionAddModalOpen = () => {
    setSubscriptionAddModalOpen(true)
  }

  const handleSubscriptionAddModalClose = () => {
    setSubscription(undefined)
    setSubscriptionAddModalOpen(false)
  }

  const handleSubscriptionEditModalOpen = (subscriptionDetail: SubscriptionDetailDto) => {
    setSubscription(subscriptionDetail)
    setSubscriptionEditModalOpen(true)
  }

  const handleSubscriptionEditModalClose = () => {
    setSubscription(undefined)
    setSubscriptionEditModalOpen(false)
  }

  const handleSubscriptionDeleteModalOpen = (subscriptionDetail: SubscriptionDetailDto) => {
    setSubscription(subscriptionDetail)
    setSubscriptionDeleteModalOpen(true)
  }

  const handleSubscriptionDeleteModalClose = () => {
    setSubscription(undefined)
    setSubscriptionDeleteModalOpen(false)
  }

  const handleSubscriptionDelete = async () => {
    if (subscription) {
      await tradingAccountsApiClient.deleteTradingAccountSubscriptionById(subscription.id)
      await onRefresh()
      setSubscription(undefined)
      setSubscriptionDeleteModalOpen(false)
    }
  }

  return (
    <div>
      {isSubscriptionAddModalOpen && (
        <Modal
          width={600}
          visible={isSubscriptionAddModalOpen}
          onClose={handleSubscriptionAddModalClose}
        >
          <SubscriptionsDetailsMainForm
            subscription={subscription}
            tradingAccountSubscriptionTypes={tradingAccountSubscriptionTypes}
            onSubmit={handleSubmitForm}
          />
        </Modal>
      )}
      {isSubscriptionEditModalOpen && (
        <Modal
          width={600}
          visible={isSubscriptionEditModalOpen}
          onClose={handleSubscriptionEditModalClose}
        >
          <SubscriptionsDetailsMainForm
            subscription={subscription}
            tradingAccountSubscriptionTypes={tradingAccountSubscriptionTypes}
            onSubmit={handleSubmitForm}
          />
        </Modal>
      )}
      {isSubscriptionDeleteModalOpen && (
        <ConfirmationModal
          contentText={`Are you sure you want to delete the following information from the subscription? Name: ${subscription?.name}`}
          yesButtonText='Yes'
          onYes={handleSubscriptionDelete}
          onCancel={handleSubscriptionDeleteModalClose}
          visible={isSubscriptionDeleteModalOpen}
        />
      )}
      {subscriptionDetails && (
        <SubscriptionsDetailsMainTable
          subscriptionDetails={subscriptionDetails.details}
          onSubscriptionAddModalOpen={handleSubscriptionAddModalOpen}
          onSubscriptionEditModalOpen={handleSubscriptionEditModalOpen}
          onSubscriptionDeleteModalOpen={handleSubscriptionDeleteModalOpen}
        />
      )}
    </div>
  )
}
